import {MouseEvent} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";
import {css as pandaCss, cx} from "@panda/css";
import {useFormikContext} from "formik";

import {m, mt} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {FormikForm} from "@pg-mono/formik-utils";
import {RequestState} from "@pg-mono/request-state";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {ApplicationEmailVerificationPopover} from "../../../application/components/ApplicationEmailVerificationPopover";
import {LeadButtonTerms} from "../../../application/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../application/components/LeadRodoTerms";
import {mktOptinsCopy} from "../../../application/constants/lead/mkt_optins_copy";
import {hitMktOptinClick} from "../../../application/tracking/application_notification_consent_hit_mkt_optin_click";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useEmailVerification} from "../../../user/hooks/use_email_verification";
import {IFavouritesListApplicationFormValues} from "./IFavouritesListApplicationFormValues";

const favouritesApplicationButtonText = "Wyślij";

export const FavouritesApplicationForm = () => {
    const variables = useSelector((state: IRPStore) => state.variables.data ?? null);
    const {data: userProfileData} = useGetUserProfileQuery();
    const {isSubmitting, isValid, handleBlur, submitForm, values} = useFormikContext<IFavouritesListApplicationFormValues>();
    const {list: favouriteProperties, fetchRequest: favouritePropertiesRequest} = useSelector((store: IRPStore) => store.myOffers.favourites.properties);
    const {list: favouriteOffers, fetchRequest: favouriteOffersRequest} = useSelector((store: IRPStore) => store.myOffers.favourites.offers);

    const {props: emailNotificationProps, validateEmailOnce} = useEmailVerification();

    const submitDisabled =
        favouriteProperties.length === 0 &&
        favouriteOffers.length === 0 &&
        favouritePropertiesRequest === RequestState.Success &&
        favouriteOffersRequest === RequestState.Success;

    const onSubmit = async () => {
        if (values?.email && !(await validateEmailOnce(values?.email))) {
            return;
        }

        return submitForm();
    };

    if (!variables) {
        return <Loader />;
    }

    return (
        <div>
            <div css={formStyle}>
                <FormikForm.Input css={fieldStyle} name="name" placeholder="Imię i nazwisko" onBlur={handleBlur} />
                <ApplicationEmailVerificationPopover {...emailNotificationProps} popoverPlace="left" zIndex={10000}>
                    <FormikForm.Input css={fieldStyle} name="email" placeholder="Email" onBlur={handleBlur} />
                </ApplicationEmailVerificationPopover>
                <FormikForm.PhoneField css={fieldStyle} name="phone" codes={variables.country_codes} placeholder="Numer telefonu" className="bt" />
                <FavouritesApplicationOptIns
                    showMktSmsField={!userProfileData?.phone_marketing_consent}
                    showMktEmailField={!userProfileData?.email_marketing_consent}
                />
                <FormikForm.Button css={mt(2)} type="button" disabled={!isValid || isSubmitting || submitDisabled} variant="filled_primary" onClick={onSubmit}>
                    {favouritesApplicationButtonText}
                </FormikForm.Button>
            </div>
            <div className={leadTerms}>
                <LeadButtonTerms buttonText={favouritesApplicationButtonText} />
                <LeadRodoTerms />
            </div>
        </div>
    );
};

interface IFavouritesApplicationOptInsProps {
    showMktSmsField: boolean;
    showMktEmailField: boolean;
    className?: string;
}

const FavouritesApplicationOptIns = (props: IFavouritesApplicationOptInsProps) => {
    const {showMktSmsField, showMktEmailField, className} = props;
    const {errors, touched} = useFormikContext<IFavouritesListApplicationFormValues>();

    if (!showMktEmailField && !showMktSmsField) {
        return null;
    }

    return (
        <div className={cx(className, optinsWrap)}>
            <Text variant="info_txt_2" as="span">
                {mktOptinsCopy}
            </Text>
            {showMktEmailField && (
                <div className={emailCheckboxWrap}>
                    <FormikForm.Checkbox
                        id="mkt_email_favourites_list"
                        name="marketing_email"
                        labelContent={
                            <Text as="span" variant="info_txt_1" dataTestId={OFFER_TEST_IDS.LEAD_FORM.EMAIL_CONSENT_CHECKBOX}>
                                o adres email
                                <Text as="span" variant="info_txt_1" className={redStar}>
                                    *
                                </Text>
                            </Text>
                        }
                        css={checkboxStyle}
                        onClick={(event: MouseEvent<HTMLInputElement>) => {
                            hitMktOptinClick(event.currentTarget.checked, "email");
                        }}
                    />
                    {errors.marketing_email && touched.marketing_email && (
                        <Text as="div" variant="info_txt_3" className={errorText}>
                            {errors.marketing_email}
                        </Text>
                    )}
                </div>
            )}
            {showMktSmsField && (
                <FormikForm.Checkbox
                    id="mkt_sms_favourites_list"
                    name="marketing_sms"
                    labelContent={
                        <Text as="span" variant="info_txt_1" dataTestId={OFFER_TEST_IDS.LEAD_FORM.PHONE_CONSENT_CHECKBOX}>
                            o numer telefonu
                        </Text>
                    }
                    css={checkboxStyle}
                    onClick={(event: MouseEvent<HTMLInputElement>) => {
                        hitMktOptinClick(event.currentTarget.checked, "sms");
                    }}
                />
            )}
        </div>
    );
};

// Styles
const formStyle = css`
    display: flex;
    flex-direction: column;
`;

const fieldStyle = css`
    ${m(0, 0, 2, 0)}
`;

const leadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    py: 2
});

const optinsWrap = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const redStar = pandaCss({
    color: "var(--colors-danger)"
});

const errorText = pandaCss({
    color: "var(--colors-danger)",
    marginTop: "0.25rem"
});

const checkboxStyle = pandaCss({
    alignItems: "flex-start"
});

const emailCheckboxWrap = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
import {MouseEvent} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";
import {css as pandaCss, cx} from "@panda/css";
import {useFormikContext} from "formik";

import {m, mt} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {FormikForm} from "@pg-mono/formik-utils";
import {RequestState} from "@pg-mono/request-state";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {ApplicationEmailVerificationPopover} from "../../../application/components/ApplicationEmailVerificationPopover";
import {LeadButtonTerms} from "../../../application/components/LeadButtonTerms";
import {LeadRodoTerms} from "../../../application/components/LeadRodoTerms";
import {mktOptinsCopy} from "../../../application/constants/lead/mkt_optins_copy";
import {hitMktOptinClick} from "../../../application/tracking/application_notification_consent_hit_mkt_optin_click";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useEmailVerification} from "../../../user/hooks/use_email_verification";
import {IFavouritesListApplicationFormValues} from "./IFavouritesListApplicationFormValues";

const favouritesApplicationButtonText = "Wyślij";

export const FavouritesApplicationForm = () => {
    const variables = useSelector((state: IRPStore) => state.variables.data ?? null);
    const {data: userProfileData} = useGetUserProfileQuery();
    const {isSubmitting, isValid, handleBlur, submitForm, values} = useFormikContext<IFavouritesListApplicationFormValues>();
    const {list: favouriteProperties, fetchRequest: favouritePropertiesRequest} = useSelector((store: IRPStore) => store.myOffers.favourites.properties);
    const {list: favouriteOffers, fetchRequest: favouriteOffersRequest} = useSelector((store: IRPStore) => store.myOffers.favourites.offers);

    const {props: emailNotificationProps, validateEmailOnce} = useEmailVerification();

    const submitDisabled =
        favouriteProperties.length === 0 &&
        favouriteOffers.length === 0 &&
        favouritePropertiesRequest === RequestState.Success &&
        favouriteOffersRequest === RequestState.Success;

    const onSubmit = async () => {
        if (values?.email && !(await validateEmailOnce(values?.email))) {
            return;
        }

        return submitForm();
    };

    if (!variables) {
        return <Loader />;
    }

    return (
        <div>
            <div css={formStyle}>
                <FormikForm.Input css={fieldStyle} name="name" placeholder="Imię i nazwisko" onBlur={handleBlur} />
                <ApplicationEmailVerificationPopover {...emailNotificationProps} popoverPlace="left" zIndex={10000}>
                    <FormikForm.Input css={fieldStyle} name="email" placeholder="Email" onBlur={handleBlur} />
                </ApplicationEmailVerificationPopover>
                <FormikForm.PhoneField css={fieldStyle} name="phone" codes={variables.country_codes} placeholder="Numer telefonu" className="bt" />
                <FavouritesApplicationOptIns
                    showMktSmsField={!userProfileData?.phone_marketing_consent}
                    showMktEmailField={!userProfileData?.email_marketing_consent}
                />
                <FormikForm.Button css={mt(2)} type="button" disabled={!isValid || isSubmitting || submitDisabled} variant="filled_primary" onClick={onSubmit}>
                    {favouritesApplicationButtonText}
                </FormikForm.Button>
            </div>
            <div className={leadTerms}>
                <LeadButtonTerms buttonText={favouritesApplicationButtonText} />
                <LeadRodoTerms />
            </div>
        </div>
    );
};

interface IFavouritesApplicationOptInsProps {
    showMktSmsField: boolean;
    showMktEmailField: boolean;
    className?: string;
}

const FavouritesApplicationOptIns = (props: IFavouritesApplicationOptInsProps) => {
    const {showMktSmsField, showMktEmailField, className} = props;
    const {errors, touched} = useFormikContext<IFavouritesListApplicationFormValues>();

    if (!showMktEmailField && !showMktSmsField) {
        return null;
    }

    return (
        <div className={cx(className, optinsWrap)}>
            <Text variant="info_txt_2" as="span">
                {mktOptinsCopy}
            </Text>
            {showMktEmailField && (
                <div className={emailCheckboxWrap}>
                    <FormikForm.Checkbox
                        id="mkt_email_favourites_list"
                        name="marketing_email"
                        labelContent={
                            <Text as="span" variant="info_txt_1" dataTestId={OFFER_TEST_IDS.LEAD_FORM.EMAIL_CONSENT_CHECKBOX}>
                                o adres email
                                <Text as="span" variant="info_txt_1" className={redStar}>
                                    *
                                </Text>
                            </Text>
                        }
                        css={checkboxStyle}
                        onClick={(event: MouseEvent<HTMLInputElement>) => {
                            hitMktOptinClick(event.currentTarget.checked, "email");
                        }}
                    />
                    {errors.marketing_email && touched.marketing_email && (
                        <Text as="div" variant="info_txt_3" className={errorText}>
                            {errors.marketing_email}
                        </Text>
                    )}
                </div>
            )}
            {showMktSmsField && (
                <FormikForm.Checkbox
                    id="mkt_sms_favourites_list"
                    name="marketing_sms"
                    labelContent={
                        <Text as="span" variant="info_txt_1" dataTestId={OFFER_TEST_IDS.LEAD_FORM.PHONE_CONSENT_CHECKBOX}>
                            o numer telefonu
                        </Text>
                    }
                    css={checkboxStyle}
                    onClick={(event: MouseEvent<HTMLInputElement>) => {
                        hitMktOptinClick(event.currentTarget.checked, "sms");
                    }}
                />
            )}
        </div>
    );
};

// Styles
const formStyle = css`
    display: flex;
    flex-direction: column;
`;

const fieldStyle = css`
    ${m(0, 0, 2, 0)}
`;

const leadTerms = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    py: 2
});

const optinsWrap = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const redStar = pandaCss({
    color: "var(--colors-danger)"
});

const errorText = pandaCss({
    color: "var(--colors-danger)",
    marginTop: "0.25rem"
});

const checkboxStyle = pandaCss({
    alignItems: "flex-start"
});

const emailCheckboxWrap = pandaCss({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
